/**
 * progressTracker.ts
 * Persists user learning progress to localStorage.
 * Updated whenever a simulation session completes.
 */

export interface ConceptProgress {
  id: string;
  name: string;
  category: string;
  mastery: number;       // 0–100
  problems: number;      // sim sessions that exercised this concept
  lastPracticed: string; // ISO date string
  trend: 'up' | 'down' | 'flat';
}

export interface SkillScore {
  axis: string;
  current: number; // 0–100
  average: number; // fixed peer average
}

export interface UserProgress {
  problemsSolved: number;
  simulationRuns: number;
  totalXP: number;
  currentStreak: number;
  bestStreak: number;
  lastActiveDate: string; // YYYY-MM-DD
  xpToNextLevel: number;
  level: number;
  concepts: ConceptProgress[];
  skillScores: SkillScore[];
  /** Map of YYYY-MM-DD → simulation count */
  activityLog: Record<string, number>;
}

// ── Component-type → concept mapping ─────────────────────────────────────────
const COMPONENT_CONCEPT_MAP: Record<string, string[]> = {
  cdn:           ['concept-cdn'],
  'load-balancer': ['concept-lb'],
  cache:         ['concept-redis'],
  database:      ['concept-db-replica', 'concept-sharding'],
  'message-queue': ['concept-kafka'],
  'api-gateway': ['concept-lb', 'concept-microservices'],
  microservice:  ['concept-microservices'],
  'circuit-breaker': ['concept-circuit'],
  'rate-limiter': ['concept-lb'],
  grpc:          ['concept-grpc'],
  auth:          ['concept-oauth'],
  tracing:       ['concept-tracing'],
};

// ── Component-type → skill axis mapping ──────────────────────────────────────
const COMPONENT_SKILL_MAP: Record<string, string[]> = {
  cdn:           ['CDN / Edge'],
  'load-balancer': ['Load Balancing'],
  cache:         ['Caching'],
  database:      ['Databases'],
  'message-queue': ['Messaging'],
  'api-gateway': ['Load Balancing'],
  microservice:  ['Load Balancing'],
  'circuit-breaker': ['Reliability'],
  'rate-limiter': ['Load Balancing'],
  grpc:          ['Databases'],
  auth:          ['Security'],
  tracing:       ['Observability'],
};

const STORAGE_KEY = 'archsim_user_progress';

const DEFAULT_CONCEPTS: ConceptProgress[] = [
  { id: 'concept-cdn',          name: 'CDN & Edge Caching',         category: 'Edge',         mastery: 0,  problems: 0, lastPracticed: 'Never', trend: 'flat' },
  { id: 'concept-lb',           name: 'Load Balancing Strategies',  category: 'Traffic',      mastery: 0,  problems: 0, lastPracticed: 'Never', trend: 'flat' },
  { id: 'concept-redis',        name: 'Redis Cache Patterns',       category: 'Cache',        mastery: 0,  problems: 0, lastPracticed: 'Never', trend: 'flat' },
  { id: 'concept-db-replica',   name: 'DB Read Replicas',           category: 'Database',     mastery: 0,  problems: 0, lastPracticed: 'Never', trend: 'flat' },
  { id: 'concept-kafka',        name: 'Event Streaming (Kafka)',     category: 'Messaging',    mastery: 0,  problems: 0, lastPracticed: 'Never', trend: 'flat' },
  { id: 'concept-microservices',name: 'Microservice Decomposition',  category: 'Compute',      mastery: 0,  problems: 0, lastPracticed: 'Never', trend: 'flat' },
  { id: 'concept-circuit',      name: 'Circuit Breaker Pattern',    category: 'Reliability',  mastery: 0,  problems: 0, lastPracticed: 'Never', trend: 'flat' },
  { id: 'concept-sharding',     name: 'Database Sharding',          category: 'Database',     mastery: 0,  problems: 0, lastPracticed: 'Never', trend: 'flat' },
  { id: 'concept-grpc',         name: 'gRPC & Protobuf',            category: 'Distributed',  mastery: 0,  problems: 0, lastPracticed: 'Never', trend: 'flat' },
  { id: 'concept-oauth',        name: 'OAuth 2.0 / JWT Auth',       category: 'Security',     mastery: 0,  problems: 0, lastPracticed: 'Never', trend: 'flat' },
  { id: 'concept-tracing',      name: 'Distributed Tracing',        category: 'Observability',mastery: 0,  problems: 0, lastPracticed: 'Never', trend: 'flat' },
  { id: 'concept-consensus',    name: 'Consensus Algorithms',       category: 'Distributed',  mastery: 0,  problems: 0, lastPracticed: 'Never', trend: 'flat' },
];

const DEFAULT_SKILLS: SkillScore[] = [
  { axis: 'Caching',        current: 0,  average: 55 },
  { axis: 'Databases',      current: 0,  average: 60 },
  { axis: 'Messaging',      current: 0,  average: 45 },
  { axis: 'CDN / Edge',     current: 0,  average: 50 },
  { axis: 'Load Balancing', current: 0,  average: 65 },
  { axis: 'Reliability',    current: 0,  average: 48 },
  { axis: 'Security',       current: 0,  average: 52 },
  { axis: 'Observability',  current: 0,  average: 44 },
];

function getDefaultProgress(): UserProgress {
  return {
    problemsSolved: 0,
    simulationRuns: 0,
    totalXP: 0,
    currentStreak: 0,
    bestStreak: 0,
    lastActiveDate: '',
    xpToNextLevel: 500,
    level: 1,
    concepts: DEFAULT_CONCEPTS,
    skillScores: DEFAULT_SKILLS,
    activityLog: {},
  };
}

export function loadProgress(): UserProgress {
  if (typeof window === 'undefined') return getDefaultProgress();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return getDefaultProgress();
    const parsed = JSON.parse(raw) as UserProgress;
    // Merge in any new concepts/skills that didn't exist when data was saved
    const existingConceptIds = new Set(parsed.concepts.map(c => c.id));
    for (const def of DEFAULT_CONCEPTS) {
      if (!existingConceptIds.has(def.id)) parsed.concepts.push({ ...def });
    }
    const existingSkillAxes = new Set(parsed.skillScores.map(s => s.axis));
    for (const def of DEFAULT_SKILLS) {
      if (!existingSkillAxes.has(def.axis)) parsed.skillScores.push({ ...def });
    }
    return parsed;
  } catch {
    return getDefaultProgress();
  }
}

function saveProgress(p: UserProgress): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  // Async sync to Firebase (fire-and-forget — no await to keep this sync)
  import('@/lib/progressService').then(({ saveProgressToFirebase }) => {
    saveProgressToFirebase(p).catch(() => {
      // Silently ignore — localStorage is the fallback
    });
  });
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatLastPracticed(isoDate: string): string {
  if (!isoDate) return 'Never';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(isoDate);
  d.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return '1 week ago';
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
}

function computeLevel(xp: number): { level: number; xpToNext: number } {
  // XP thresholds: level n requires n*500 XP total
  const level = Math.max(1, Math.floor(xp / 500) + 1);
  const xpToNext = level * 500 - xp;
  return { level, xpToNext };
}

/**
 * Called when a simulation session ends (user stops the sim).
 * @param componentDefIds  Array of defId strings for nodes on the canvas
 * @param simDurationTicks Number of ticks the simulation ran
 * @param avgErrorRate     Average error rate during the session (0–1)
 */
export function recordSimulationSession(
  componentDefIds: string[],
  simDurationTicks: number,
  avgErrorRate: number,
): UserProgress {
  const progress = loadProgress();
  const today = todayStr();

  // ── 1. Simulation run count + activity log ────────────────────────────────
  progress.simulationRuns += 1;
  progress.activityLog[today] = (progress.activityLog[today] ?? 0) + 1;

  // ── 2. Streak calculation ─────────────────────────────────────────────────
  if (progress.lastActiveDate !== today) {
    const yesterday = (() => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();
    if (progress.lastActiveDate === yesterday) {
      progress.currentStreak += 1;
    } else if (progress.lastActiveDate !== today) {
      progress.currentStreak = 1;
    }
    progress.bestStreak = Math.max(progress.bestStreak, progress.currentStreak);
    progress.lastActiveDate = today;
  }

  // ── 3. XP earned ─────────────────────────────────────────────────────────
  // Base XP: 10 per tick, bonus for longer sessions, penalty for high error rate
  const baseXP = Math.min(simDurationTicks * 10, 200);
  const errorPenalty = Math.floor(baseXP * avgErrorRate * 0.5);
  const xpEarned = Math.max(5, baseXP - errorPenalty);
  progress.totalXP += xpEarned;

  // ── 4. Problems solved: count unique component types used ─────────────────
  const uniqueTypes = new Set(componentDefIds);
  if (uniqueTypes.size >= 2 && simDurationTicks >= 3) {
    progress.problemsSolved += 1;
  }

  // ── 5. Level ──────────────────────────────────────────────────────────────
  const { level, xpToNext } = computeLevel(progress.totalXP);
  progress.level = level;
  progress.xpToNextLevel = xpToNext;

  // ── 6. Concept mastery ────────────────────────────────────────────────────
  const exercisedConceptIds = new Set<string>();
  for (const defId of componentDefIds) {
    // Match by prefix/substring — defId may be like 'cdn-node', 'cache-redis', etc.
    for (const [key, conceptIds] of Object.entries(COMPONENT_CONCEPT_MAP)) {
      if (defId.toLowerCase().includes(key.toLowerCase())) {
        conceptIds.forEach(c => exercisedConceptIds.add(c));
      }
    }
  }

  progress.concepts = progress.concepts.map(concept => {
    if (!exercisedConceptIds.has(concept.id)) return concept;
    const prevMastery = concept.mastery;
    // Mastery gain: diminishing returns as mastery increases
    const gain = Math.max(1, Math.floor((100 - concept.mastery) * 0.08));
    const newMastery = Math.min(100, concept.mastery + gain);
    return {
      ...concept,
      mastery: newMastery,
      problems: concept.problems + 1,
      lastPracticed: today,
      trend: newMastery > prevMastery ? 'up' : newMastery < prevMastery ? 'down' : 'flat',
    };
  });

  // ── 7. Skill radar scores ─────────────────────────────────────────────────
  const exercisedAxes = new Set<string>();
  for (const defId of componentDefIds) {
    for (const [key, axes] of Object.entries(COMPONENT_SKILL_MAP)) {
      if (defId.toLowerCase().includes(key.toLowerCase())) {
        axes.forEach(a => exercisedAxes.add(a));
      }
    }
  }

  progress.skillScores = progress.skillScores.map(skill => {
    if (!exercisedAxes.has(skill.axis)) return skill;
    const gain = Math.max(1, Math.floor((100 - skill.current) * 0.06));
    return { ...skill, current: Math.min(100, skill.current + gain) };
  });

  saveProgress(progress);
  return progress;
}

/** Returns a human-readable lastPracticed string for each concept */
export function getConceptsWithRelativeDates(progress: UserProgress): ConceptProgress[] {
  return progress.concepts.map(c => ({
    ...c,
    lastPracticed: c.lastPracticed && c.lastPracticed !== 'Never'
      ? formatLastPracticed(c.lastPracticed)
      : 'Never',
  }));
}
